import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Chip,
  Alert,
  LinearProgress,
  IconButton,
  Tooltip,
  CircularProgress,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Error as ErrorIcon,
  DeleteOutline,
  FileDownload,
  ArrowBack,
  TableChart,
  UploadFile,
  PlayArrow,
  HourglassEmpty,
  Insights,
  Close,
} from '@mui/icons-material';
import { batchAPI, viewsAPI } from '../services/api';
import { BatchJobDetail, BatchRowStatus } from '../types';

// ── Wealth pyramid (batch results) ──────────────────────────────────────────────

const BATCH_PYRAMID_TIERS: { label: string; color: string; bands: string[] }[] = [
  { label: '$250,000+',         color: '#0E6655', bands: ['$250,000+'] },
  { label: '$150,000–$249,999', color: '#1A8A72', bands: ['$200,000-$249,999', '$175,000-$199,999', '$150,000-$174,999'] },
  { label: '$100,000–$149,999', color: '#27AE93', bands: ['$125,000-$149,999', '$100,000-$124,999'] },
  { label: '$75,000–$99,999',   color: '#2EC4A5', bands: ['$75,000-$99,999'] },
  { label: '$50,000–$74,999',   color: '#F4A62A', bands: ['$50,000-$74,999'] },
  { label: '$35,000–$49,999',   color: '#E07B20', bands: ['$35,000-$49,999'] },
  { label: 'Under $35,000',     color: '#C75A18', bands: ['$25,000-$34,999', '$15,000-$24,999', '$1,000-$14,999'] },
  { label: 'Unknown / N/A',     color: '#B0BEC5', bands: [] },
];

function getBatchRowTier(income: string | null | undefined): string {
  if (!income) return 'Unknown / N/A';
  for (const tier of BATCH_PYRAMID_TIERS) {
    if (tier.bands.includes(income)) return tier.label;
  }
  return 'Unknown / N/A';
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface CSVRow {
  rowIndex: number;
  first_name: string;
  last_name: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  valid: boolean;
  errors: string[];
}

interface ParseResult {
  rows: CSVRow[];
  totalRows: number;
  validRows: number;
  invalidRows: number;
  detectedColumns: string[];
}

// ─── CSV column aliases ────────────────────────────────────────────────────────
// Maps common header variations to our internal field names
const COLUMN_MAP: Record<string, string> = {
  firstname: 'first_name',
  first_name: 'first_name',
  'first name': 'first_name',
  fname: 'first_name',
  lastname: 'last_name',
  last_name: 'last_name',
  'last name': 'last_name',
  lname: 'last_name',
  street: 'street',
  street1: 'street',
  address: 'street',
  address1: 'street',
  streetaddress: 'street',
  'street address': 'street',
  city: 'city',
  state: 'state',
  zip: 'zip',
  zipcode: 'zip',
  zip_code: 'zip',
  'zip code': 'zip',
  postal: 'zip',
  postalcode: 'zip',
};

// ─── Parse CSV text ───────────────────────────────────────────────────────────
function parseCSV(text: string): ParseResult {
  const lines = text.split(/\r?\n/).filter(l => l.trim() !== '');
  if (lines.length < 2) {
    return { rows: [], totalRows: 0, validRows: 0, invalidRows: 0, detectedColumns: [] };
  }

  // Parse header
  const rawHeaders = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
  const detectedColumns = rawHeaders;

  // Normalize header -> field mapping
  const headerIndexMap: Record<string, number> = {};
  rawHeaders.forEach((h, i) => {
    const normalized = h.toLowerCase().replace(/[^a-z0-9 _]/g, '');
    const mapped = COLUMN_MAP[normalized];
    if (mapped) headerIndexMap[mapped] = i;
  });

  const rows: CSVRow[] = [];

  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(',').map(c => c.trim().replace(/^"|"$/g, ''));
    const get = (field: string) => {
      const idx = headerIndexMap[field];
      return idx !== undefined ? (cols[idx] || '').trim() : '';
    };

    const first_name = get('first_name');
    const last_name = get('last_name');
    const street = get('street');
    const city = get('city');
    const state = get('state');
    const zip = get('zip');

    const errors: string[] = [];
    if (!last_name) errors.push('Last name required');
    if (!street && !city && !zip) errors.push('At least address, city or ZIP required');

    rows.push({
      rowIndex: i,
      first_name,
      last_name,
      street,
      city,
      state,
      zip,
      valid: errors.length === 0,
      errors,
    });
  }

  return {
    rows,
    totalRows: rows.length,
    validRows: rows.filter(r => r.valid).length,
    invalidRows: rows.filter(r => !r.valid).length,
    detectedColumns,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

interface BatchPageProps {
  onBack: () => void;
  onViewCreated: (viewId: number) => void;
  onMyViews: () => void;
}

const BatchPage: React.FC<BatchPageProps> = ({ onBack, onViewCreated, onMyViews }) => {
  // Upload / parse state
  const [isDragging, setIsDragging] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Row selection (Set of rowIndex values for valid rows)
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());

  // Auto-select all valid rows when a new file is parsed
  useEffect(() => {
    if (parseResult) {
      setSelectedRows(new Set(parseResult.rows.filter(r => r.valid).map(r => r.rowIndex)));
    }
  }, [parseResult]);

  // Selection helpers
  const validRowIndices = parseResult ? parseResult.rows.filter(r => r.valid).map(r => r.rowIndex) : [];
  const allSelected = validRowIndices.length > 0 && validRowIndices.every(i => selectedRows.has(i));
  const someSelected = validRowIndices.some(i => selectedRows.has(i)) && !allSelected;
  const selectedCount = validRowIndices.filter(i => selectedRows.has(i)).length;

  const handleToggleRow = (rowIndex: number) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowIndex)) next.delete(rowIndex); else next.add(rowIndex);
      return next;
    });
  };

  const handleToggleAll = () => {
    if (allSelected) {
      setSelectedRows(prev => { const next = new Set(prev); validRowIndices.forEach(i => next.delete(i)); return next; });
    } else {
      setSelectedRows(prev => { const next = new Set(prev); validRowIndices.forEach(i => next.add(i)); return next; });
    }
  };

  // Batch job state
  const [jobId, setJobId] = useState<number | null>(null);
  const [job, setJob] = useState<BatchJobDetail | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Results table pagination
  const [resultsPage, setResultsPage] = useState(0);
  const [resultsRowsPerPage, setResultsRowsPerPage] = useState(100);

  // Restore in-progress job from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('kc:batch:active_job');
    if (!saved) return;
    try {
      const { jobId: savedId, startedAt } = JSON.parse(saved);
      const ageMs = Date.now() - new Date(startedAt).getTime();
      if (ageMs > 24 * 60 * 60 * 1000) { localStorage.removeItem('kc:batch:active_job'); return; }
      setJobId(savedId);
      startPolling(savedId);
    } catch { localStorage.removeItem('kc:batch:active_job'); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Next-stage selection (row IDs of completed rows selected to advance)
  const [nextStageRows, setNextStageRows] = useState<Set<number>>(new Set());

  // Filter panel
  // income band checkboxes — null means "all selected" (no filter applied)
  const [incomeFilter, setIncomeFilter] = useState<Set<string> | null>(null);
  const [minAffluence, setMinAffluence] = useState('');
  const [maxAffluence, setMaxAffluence] = useState('');
  const [minWealth, setMinWealth] = useState('');
  const [maxWealth, setMaxWealth] = useState('');
  const [homeValueRange, setHomeValueRange] = useState<'lower' | 'upper' | null>(null);

  const clearAllFilters = () => {
    setIncomeFilter(null);
    setMinAffluence(''); setMaxAffluence('');
    setMinWealth(''); setMaxWealth('');
    setHomeValueRange(null);
  };

  // derived: is any filter active?
  const isFilterActive = incomeFilter !== null || minAffluence !== '' || maxAffluence !== '' || minWealth !== '' || maxWealth !== '' || homeValueRange !== null;

  // ── Create View dialog ──────────────────────────────────────────────
  const [createViewOpen, setCreateViewOpen] = useState(false);
  const [viewName, setViewName] = useState('');
  const [isCreatingView, setIsCreatingView] = useState(false);
  const [createViewError, setCreateViewError] = useState<string | null>(null);

  const handleCreateView = async () => {
    if (!viewName.trim() || !job) return;
    setIsCreatingView(true);
    setCreateViewError(null);
    try {
      const rowsToSave = isFilterActive ? pyramidFilteredRows : job.rows.filter(r => r.status === 'completed' && nextStageRows.has(r.id));
      const selectedMembers = rowsToSave.map(r => ({
          batch_job_row_id: r.id,
          first_name: r.first_name ?? undefined,
          last_name: r.last_name,
          street: r.street ?? undefined,
          city: r.city ?? undefined,
          state: r.state ?? undefined,
          zip: r.zip ?? undefined,
          affluence_score: r.affluence_score ?? undefined,
          wealth_iq_score: r.wealth_iq_score ?? undefined,
          est_hh_income: r.est_hh_income ?? undefined,
          home_value: r.home_value ?? undefined,
        }));
      const resp = await viewsAPI.create({
        name: viewName.trim(),
        source_batch_job_id: jobId ?? undefined,
        members: selectedMembers,
      });
      setCreateViewOpen(false);
      setViewName('');
      onViewCreated(resp.view_id);
    } catch (err: any) {
      setCreateViewError(err.message || 'Failed to create view');
    } finally {
      setIsCreatingView(false);
    }
  };

  // ── Results table sort ──────────────────────────────────────────────────
  type SortField = 'last_name' | 'affluence_score' | 'wealth_iq_score' | 'est_hh_income' | 'home_value';
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setResultsPage(0);
  };

  // ── Preview table sort ─────────────────────────────────────────────────
  type PreviewSortField = 'first_name' | 'last_name' | 'street' | 'city' | 'state' | 'zip';
  const [previewSortField, setPreviewSortField] = useState<PreviewSortField | null>(null);
  const [previewSortDir, setPreviewSortDir] = useState<'asc' | 'desc'>('asc');

  const handlePreviewSort = (field: PreviewSortField) => {
    if (previewSortField === field) {
      setPreviewSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setPreviewSortField(field);
      setPreviewSortDir('asc');
    }
  };

  // Auto-select all completed rows once the job finishes
  useEffect(() => {
    if ((job?.status === 'completed' || job?.status === 'failed') && job?.rows) {
      setNextStageRows(new Set(job.rows.filter(r => r.status === 'completed').map(r => r.id)));
    }
  }, [job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleNextStage = (id: number) => {
    setNextStageRows(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const completedRows = job?.rows?.filter(r => r.status === 'completed') ?? [];
  const allNextSelected = completedRows.length > 0 && completedRows.every(r => nextStageRows.has(r.id));
  const someNextSelected = completedRows.some(r => nextStageRows.has(r.id)) && !allNextSelected;
  const handleToggleAllNext = () => {
    if (allNextSelected) setNextStageRows(prev => { const n = new Set(prev); completedRows.forEach(r => n.delete(r.id)); return n; });
    else setNextStageRows(prev => { const n = new Set(prev); completedRows.forEach(r => n.add(r.id)); return n; });
  };

  // Derived step
  const activeStep = jobId
    ? (job?.status === 'completed' || job?.status === 'failed') ? 4 : 3
    : !parseResult ? 1 : 2;
  const isJobDone = job?.status === 'completed' || job?.status === 'failed';
  const progressPct = job && job.total_rows > 0 ? Math.round(((job.completed_rows + job.failed_rows) / job.total_rows) * 100) : 0;

  // ── Polling ───────────────────────────────────────────────────────────────────
  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  useEffect(() => { return () => stopPolling(); }, [stopPolling]);

  const startPolling = useCallback((id: number) => {
    stopPolling();
    const poll = async () => {
      try {
        const detail = await batchAPI.getJob(id);
        setJob(detail);
        if (detail.status === 'completed' || detail.status === 'failed') stopPolling();
      } catch { /* silent — keep polling */ }
    };
    poll();
    pollRef.current = setInterval(poll, 2000);
  }, [stopPolling]);

  // ── Run batch ─────────────────────────────────────────────────────────────────
  const handleRunBatch = async () => {
    if (!parseResult || !file) return;
    setIsSubmitting(true);
    setRunError(null);
    try {
      const validRows = parseResult.rows
        .filter(r => r.valid && selectedRows.has(r.rowIndex))
        .map(r => ({
          row_index:  r.rowIndex,
          first_name: r.first_name || undefined,
          last_name:  r.last_name,
          street:     r.street  || undefined,
          city:       r.city    || undefined,
          state:      r.state   || undefined,
          zip:        r.zip     || undefined,
        }));
      const resp = await batchAPI.submit(file.name, validRows);
      setJobId(resp.job_id);
      localStorage.setItem('kc:batch:active_job', JSON.stringify({ jobId: resp.job_id, startedAt: new Date().toISOString() }));
      startPolling(resp.job_id);
    } catch (err: any) {
      setRunError(err.message || 'Failed to submit batch job');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Drag events ──────────────────────────────────────────────────────────────
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) processFile(dropped);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── File input change ─────────────────────────────────────────────────────────
  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) processFile(selected);
  };

  // ── Process file ──────────────────────────────────────────────────────────────
  const processFile = (f: File) => {
    setParseError(null);
    setParseResult(null);
    setPage(0);
    setJobId(null);
    setJob(null);
    setRunError(null);
    stopPolling();

    if (!f.name.toLowerCase().endsWith('.csv')) {
      setParseError('Only .csv files are accepted. Please upload a CSV file.');
      setFile(null);
      return;
    }

    if (f.size > 10 * 1024 * 1024) { // 10 MB limit
      setParseError('File is too large. Maximum size is 10 MB.');
      setFile(null);
      return;
    }

    setFile(f);

    // Clear any saved job since a new file is being uploaded
    localStorage.removeItem('kc:batch:active_job');

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      try {
        const result = parseCSV(text);
        if (result.totalRows === 0) {
          setParseError('The CSV file appears to be empty or has no data rows.');
          setFile(null);
          return;
        }
        setParseResult(result);
      } catch {
        setParseError('Failed to parse the CSV file. Please check the file format.');
        setFile(null);
      }
    };
    reader.readAsText(f);
  };

  // ── Clear / reset ─────────────────────────────────────────────────────────────
  const handleClear = () => {
    stopPolling();
    setFile(null);
    setParseResult(null);
    setParseError(null);
    setPage(0);
    setJobId(null);
    setJob(null);
    setRunError(null);
    setIsSubmitting(false);
    setSelectedRows(new Set());
    localStorage.removeItem('kc:batch:active_job');
    setNextStageRows(new Set());
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ── Download sample CSV ───────────────────────────────────────────────────────
  const downloadSampleCSV = () => {
    const sample = [
      'FirstName,LastName,Street,City,State,ZIP',
      'John,Smith,123 Main St,Dallas,TX,75201',
      'Jane,Doe,456 Oak Avenue,Houston,TX,77001',
      'Robert,Johnson,789 Pine Rd,Austin,TX,78701',
    ].join('\n');
    const blob = new Blob([sample], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'batch_search_sample.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const sortedPreviewRows = parseResult ? [...parseResult.rows].sort((a, b) => {
    if (!previewSortField) return 0;
    const av = (a[previewSortField] ?? '').toLowerCase();
    const bv = (b[previewSortField] ?? '').toLowerCase();
    return previewSortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  }) : [];

  const paginatedRows = sortedPreviewRows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const sortedResultRows: BatchRowStatus[] = job ? (sortField ? [...job.rows].sort((a, b) => {
    const toSortable = (val: string | null | undefined): number | string => {
      if (!val) return '';
      const stripped = val.replace(/[$,]/g, '');
      const num = parseFloat(stripped);
      return isNaN(num) ? val.toLowerCase() : num;
    };
    const compareName = (x: BatchRowStatus, y: BatchRowStatus): number => {
      const firstCmp = (x.first_name ?? '').localeCompare(y.first_name ?? '');
      if (firstCmp !== 0) return firstCmp;
      return (x.last_name ?? '').localeCompare(y.last_name ?? '');
    };
    if (sortField === 'last_name') {
      return sortDir === 'asc' ? compareName(a, b) : compareName(b, a);
    }
    const fieldMap: Record<string, string | null | undefined> = {
      affluence_score: a.affluence_score,
      wealth_iq_score: a.wealth_iq_score,
      est_hh_income:   a.est_hh_income,
      home_value:      a.home_value,
    };
    const fieldMapB: Record<string, string | null | undefined> = {
      affluence_score: b.affluence_score,
      wealth_iq_score: b.wealth_iq_score,
      est_hh_income:   b.est_hh_income,
      home_value:      b.home_value,
    };
    const av = toSortable(fieldMap[sortField]);
    const bv = toSortable(fieldMapB[sortField]);
    let cmp: number;
    if (typeof av === 'number' && typeof bv === 'number') {
      cmp = av - bv;
    } else {
      cmp = String(av).localeCompare(String(bv));
    }
    if (cmp !== 0) return sortDir === 'asc' ? cmp : -cmp;
    return compareName(a, b);
  }) : job.rows) : [];

  const toNum = (v: string | null | undefined) => {
    if (!v) return null;
    const n = parseFloat(v.replace(/[^0-9.]/g, ''));
    return isNaN(n) ? null : n;
  };

  // Home value median for range filter
  const allCompletedHVs = (job?.rows ?? [])
    .filter(r => r.status === 'completed')
    .map(r => r.home_value ? parseFloat(r.home_value.replace(/[$,]/g, '')) : null)
    .filter((v): v is number => v !== null && !isNaN(v))
    .sort((a, b) => a - b);
  const hvMedianGlobal = allCompletedHVs.length > 0
    ? allCompletedHVs.length % 2 === 0
      ? (allCompletedHVs[allCompletedHVs.length / 2 - 1] + allCompletedHVs[allCompletedHVs.length / 2]) / 2
      : allCompletedHVs[Math.floor(allCompletedHVs.length / 2)]
    : null;

  const pyramidFilteredRows = sortedResultRows.filter(r => {
    // Income band filter
    if (incomeFilter !== null) {
      const tier = getBatchRowTier(r.est_hh_income);
      if (!incomeFilter.has(tier)) return false;
    }
    // Affluence score range
    const aff = toNum(r.affluence_score);
    if (minAffluence !== '' && (aff === null || aff < parseFloat(minAffluence))) return false;
    if (maxAffluence !== '' && (aff === null || aff > parseFloat(maxAffluence))) return false;
    // Wealth IQ range
    const wiq = toNum(r.wealth_iq_score);
    if (minWealth !== '' && (wiq === null || wiq < parseFloat(minWealth))) return false;
    if (maxWealth !== '' && (wiq === null || wiq > parseFloat(maxWealth))) return false;
    // Home value range
    if (homeValueRange !== null && hvMedianGlobal !== null) {
      const hv = toNum(r.home_value);
      if (hv === null) return false;
      if (homeValueRange === 'lower' && hv > hvMedianGlobal) return false;
      if (homeValueRange === 'upper' && hv <= hvMedianGlobal) return false;
    }
    return true;
  });

  const paginatedResultRows = pyramidFilteredRows.slice(
    resultsPage * resultsRowsPerPage,
    resultsPage * resultsRowsPerPage + resultsRowsPerPage
  );

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', backgroundColor: '#E5E7EB' }}>

      {/* ── Page Header ── */}
      <Box sx={{
        px: 3, py: 1.5,
        backgroundColor: '#283E56',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        flexWrap: 'nowrap',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <IconButton onClick={onBack} sx={{ color: 'white', p: 0.75, flexShrink: 0 }}>
            <ArrowBack />
          </IconButton>
          <TableChart sx={{ fontSize: 22, opacity: 0.85, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.2 }}>
              {jobId ? 'Screening Results' : 'Batch Search'}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', opacity: 0.65 }}>
              {jobId
                ? job ? `${job.completed_rows + job.failed_rows} / ${job.total_rows} processed` : 'Starting…'
                : 'Upload a CSV file to screen multiple prospects at once'
              }
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, ml: 2 }}>
          {isJobDone && (
            <>
              <Button variant="outlined" size="small" onClick={onMyViews}
                sx={{ color: 'rgba(255,255,255,0.85)', borderColor: 'rgba(255,255,255,0.3)', fontWeight: 600,
                  textTransform: 'none', fontSize: '0.82rem', whiteSpace: 'nowrap',
                  '&:hover': { color: 'white', borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.06)' } }}>
                My Views
              </Button>
              {(isFilterActive ? pyramidFilteredRows.length > 0 : nextStageRows.size > 0) && (
                <Button variant="contained" size="small"
                  onClick={() => { setCreateViewOpen(true); setCreateViewError(null); }}
                  sx={{ backgroundColor: '#00C853', fontWeight: 700, textTransform: 'none',
                    fontSize: '0.82rem', whiteSpace: 'nowrap', boxShadow: 'none',
                    '&:hover': { backgroundColor: '#009624', boxShadow: 'none' } }}>
                  Create View ({isFilterActive ? pyramidFilteredRows.length : nextStageRows.size})
                </Button>
              )}
              <Box sx={{ width: '1px', height: 22, backgroundColor: 'rgba(255,255,255,0.25)', mx: 0.5 }} />
            </>
          )}
          <Button variant="outlined" size="small"
            onClick={jobId ? handleClear : downloadSampleCSV}
            startIcon={!jobId ? <FileDownload sx={{ fontSize: '16px !important' }} /> : undefined}
            sx={{ color: 'rgba(255,255,255,0.85)', borderColor: 'rgba(255,255,255,0.3)', textTransform: 'none',
              fontSize: '0.82rem', whiteSpace: 'nowrap',
              '&:hover': { borderColor: 'white', color: 'white', backgroundColor: 'rgba(255,255,255,0.06)' } }}>
            {jobId ? 'Start New Batch' : 'Download Sample CSV'}
          </Button>
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>

        {/* ── Step indicator ── */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3, alignItems: 'center' }}>
          {[
            { step: 1, label: 'Upload CSV' },
            { step: 2, label: 'Preview & Validate' },
            { step: 3, label: 'Experian Screening' },
            { step: 4, label: 'Create View' },
            { step: 5, label: 'Export Results' },
          ].map((s, i, arr) => (
            <React.Fragment key={s.step}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  backgroundColor: s.step === activeStep ? '#283E56' : s.step < activeStep ? '#00C853' : '#CBD5E0',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  flexShrink: 0,
                }}>
                  {s.step < activeStep ? <CheckCircle sx={{ fontSize: 16 }} /> : s.step}
                </Box>
                <Typography variant="body2" sx={{
                  fontWeight: s.step === activeStep ? 700 : 400,
                  color: s.step <= activeStep ? '#283E56' : '#9CA3AF',
                  whiteSpace: 'nowrap',
                }}>
                  {s.label}
                </Typography>
              </Box>
              {i < arr.length - 1 && (
                <Box sx={{ flex: 1, height: 2, backgroundColor: s.step < activeStep ? '#00C853' : '#CBD5E0', minWidth: 20 }} />
              )}
            </React.Fragment>
          ))}
        </Box>

        {/* ── Upload / Preview (hidden once job is submitted) ── */}
        {!jobId && (
          <>
        {/* ── Upload Area ── */}
        {!parseResult && (
          <Paper
            elevation={0}
            sx={{
              border: `2px dashed ${isDragging ? '#283E56' : parseError ? '#ef4444' : '#CBD5E0'}`,
              borderRadius: 3,
              backgroundColor: isDragging
                ? 'rgba(40, 62, 86, 0.04)'
                : parseError
                ? 'rgba(239,68,68,0.03)'
                : 'rgba(255,255,255,0.8)',
              transition: 'all 0.2s ease',
              cursor: 'pointer',
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <Box sx={{
              py: 8,
              px: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
              textAlign: 'center',
            }}>
              <Box sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                backgroundColor: isDragging ? '#283E56' : '#EEF2F7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease',
              }}>
                {isDragging
                  ? <UploadFile sx={{ fontSize: 36, color: 'white' }} />
                  : <CloudUpload sx={{ fontSize: 36, color: '#283E56' }} />
                }
              </Box>

              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#283E56' }}>
                  {isDragging ? 'Drop your CSV file here' : 'Drag & drop your CSV file'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
                  or{' '}
                  <Typography component="span" sx={{ color: '#283E56', fontWeight: 600, textDecoration: 'underline' }}>
                    browse to upload
                  </Typography>
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center', mt: 1 }}>
                {['FirstName', 'LastName', 'Street', 'City', 'State', 'ZIP'].map(col => (
                  <Chip
                    key={col}
                    label={col}
                    size="small"
                    sx={{ backgroundColor: '#EEF2F7', color: '#283E56', fontWeight: 500, fontSize: '0.75rem' }}
                  />
                ))}
              </Box>
              <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                CSV format only · Max 10 MB · No row limit for upload
              </Typography>
            </Box>
          </Paper>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          style={{ display: 'none' }}
          onChange={handleFileInput}
        />

        {/* ── Parse error ── */}
        {parseError && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setParseError(null)}>
            {parseError}
          </Alert>
        )}

        {/* ── Run error (submit failed before job was created) ── */}
        {runError && !job && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setRunError(null)}>
            {runError}
          </Alert>
        )}

        {/* ── File uploaded — stats bar ── */}
        {parseResult && file && (
          <>
            <Paper elevation={2} sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>

                {/* File info */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CheckCircle sx={{ color: '#22c55e', fontSize: 28 }} />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#283E56' }}>
                      {file.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                      {(file.size / 1024).toFixed(1)} KB
                    </Typography>
                  </Box>
                </Box>

                {/* Stats chips */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    label={`${parseResult.totalRows} total rows`}
                    sx={{ backgroundColor: '#EEF2F7', color: '#283E56', fontWeight: 600 }}
                  />
                  <Chip
                    label={`${parseResult.validRows} valid`}
                    sx={{ backgroundColor: '#dcfce7', color: '#166534', fontWeight: 600 }}
                    icon={<CheckCircle sx={{ fontSize: '16px !important', color: '#22c55e !important' }} />}
                  />
                  {parseResult.invalidRows > 0 && (
                    <Chip
                      label={`${parseResult.invalidRows} issues`}
                      sx={{ backgroundColor: '#fef2f2', color: '#991b1b', fontWeight: 600 }}
                      icon={<ErrorIcon sx={{ fontSize: '16px !important', color: '#ef4444 !important' }} />}
                    />
                  )}
                  <Chip
                    label={`${selectedCount} selected`}
                    sx={{
                      backgroundColor: selectedCount === parseResult.validRows ? '#EEF2F7' : '#fef9c3',
                      color: selectedCount === parseResult.validRows ? '#283E56' : '#854d0e',
                      fontWeight: 600,
                      border: '1px solid',
                      borderColor: selectedCount === parseResult.validRows ? '#CBD5E0' : '#fde047',
                    }}
                  />
                </Box>

                {/* Actions */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Remove file and start over">
                    <IconButton size="small" onClick={handleClear} sx={{ color: '#EF4444' }}>
                      <DeleteOutline />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Detected columns */}
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #E5E7EB' }}>
                <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 500, mr: 1 }}>
                  Detected columns:
                </Typography>
                {parseResult.detectedColumns.map(col => (
                  <Chip
                    key={col}
                    label={col}
                    size="small"
                    sx={{ mr: 0.5, mb: 0.5, fontSize: '0.7rem', height: 22 }}
                  />
                ))}
              </Box>
            </Paper>

            {/* ── Action Bar ── */}
            <Box sx={{
              mb: 3,
              p: 2,
              backgroundColor: 'white',
              border: '1px solid #E5E7EB',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
            }}>
              <Typography variant="body2" sx={{ color: '#6B7280' }}>
                {selectedCount} of {parseResult.validRows} valid row{parseResult.validRows !== 1 ? 's' : ''} selected
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={handleClear}
                  sx={{ borderColor: '#CBD5E0', color: '#6B7280', '&:hover': { borderColor: '#283E56', color: '#283E56' } }}
                >
                  Upload Different File
                </Button>
                <Button
                  variant="contained"
                  onClick={handleRunBatch}
                  disabled={isSubmitting || selectedCount === 0}
                  startIcon={isSubmitting ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <PlayArrow />}
                  sx={{
                    backgroundColor: '#283E56',
                    '&:hover': { backgroundColor: '#1C2433' },
                    '&.Mui-disabled': { backgroundColor: '#CBD5E0', color: '#9CA3AF' },
                  }}
                >
                  {isSubmitting ? 'Submitting…' : `Run Experian Screen (${selectedCount})`}
                </Button>
              </Box>
            </Box>

            {/* ── Preview Table ── */}
            <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{
                px: 2.5,
                py: 2,
                backgroundColor: '#283E56',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Preview — {parseResult.totalRows} rows
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  {selectedCount} selected · Showing {Math.min(rowsPerPage, parseResult.totalRows)} of {parseResult.totalRows} rows · Click a row to toggle
                </Typography>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#F8FAFC' }}>
                      <TableCell padding="checkbox" sx={{ width: 48 }}>
                        <Tooltip title={allSelected ? 'Deselect all' : 'Select all valid rows'}>
                          <Checkbox
                            size="small"
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={handleToggleAll}
                            disabled={validRowIndices.length === 0}
                            sx={{ color: '#283E56', '&.Mui-checked': { color: '#283E56' }, '&.MuiCheckbox-indeterminate': { color: '#283E56' } }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#283E56', width: 50 }}>#</TableCell>
                      {(['first_name', 'last_name', 'street', 'city', 'state', 'zip'] as const).map((col) => (
                        <TableCell key={col} sortDirection={previewSortField === col ? previewSortDir : false}
                          sx={{ fontWeight: 700, color: '#283E56', whiteSpace: 'nowrap' }}>
                          <TableSortLabel
                            active={previewSortField === col}
                            direction={previewSortField === col ? previewSortDir : 'asc'}
                            onClick={() => handlePreviewSort(col)}
                            sx={{
                              fontWeight: 700, color: '#283E56 !important',
                              '& .MuiTableSortLabel-icon': { color: '#283E56 !important', opacity: previewSortField === col ? 1 : 0.35 },
                              '&:hover .MuiTableSortLabel-icon': { opacity: 1 },
                            }}
                          >
                            {col === 'first_name' ? 'First Name' : col === 'last_name' ? 'Last Name' : col === 'zip' ? 'ZIP' : col.charAt(0).toUpperCase() + col.slice(1)}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                      <TableCell sx={{ fontWeight: 700, color: '#283E56', width: 80 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedRows.map((row) => (
                      <TableRow
                        key={row.rowIndex}
                        onClick={() => row.valid && handleToggleRow(row.rowIndex)}
                        sx={{
                          backgroundColor: row.valid
                            ? selectedRows.has(row.rowIndex) ? 'rgba(40,62,86,0.04)' : 'inherit'
                            : 'rgba(239,68,68,0.04)',
                          cursor: row.valid ? 'pointer' : 'default',
                          '&:hover': { backgroundColor: row.valid ? 'rgba(40,62,86,0.07)' : 'rgba(239,68,68,0.04)' },
                        }}
                      >
                        <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                          <Checkbox
                            size="small"
                            checked={row.valid && selectedRows.has(row.rowIndex)}
                            disabled={!row.valid}
                            onChange={() => handleToggleRow(row.rowIndex)}
                            sx={{ color: '#283E56', '&.Mui-checked': { color: '#283E56' } }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: '#9CA3AF', fontSize: '0.8rem' }}>{row.rowIndex}</TableCell>
                        <TableCell>{row.first_name || <Typography component="span" sx={{ color: '#9CA3AF', fontStyle: 'italic', fontSize: '0.8rem' }}>—</Typography>}</TableCell>
                        <TableCell sx={{ fontWeight: row.last_name ? 500 : 400 }}>
                          {row.last_name || <Typography component="span" sx={{ color: '#ef4444', fontStyle: 'italic', fontSize: '0.8rem' }}>missing</Typography>}
                        </TableCell>
                        <TableCell sx={{ color: '#4B5563' }}>{row.street || '—'}</TableCell>
                        <TableCell sx={{ color: '#4B5563' }}>{row.city || '—'}</TableCell>
                        <TableCell>
                          {row.state
                            ? <Chip label={row.state} size="small" sx={{ fontSize: '0.7rem', height: 20, backgroundColor: '#EEF2F7' }} />
                            : <Typography component="span" sx={{ color: '#9CA3AF', fontSize: '0.8rem' }}>—</Typography>
                          }
                        </TableCell>
                        <TableCell sx={{ color: '#4B5563' }}>{row.zip || '—'}</TableCell>
                        <TableCell>
                          {row.valid
                            ? <Chip label="OK" size="small" sx={{ backgroundColor: '#dcfce7', color: '#166534', fontWeight: 600, fontSize: '0.7rem', height: 20 }} />
                            : (
                              <Tooltip title={row.errors.join(', ')}>
                                <Chip label="Issue" size="small" sx={{ backgroundColor: '#fef2f2', color: '#991b1b', fontWeight: 600, fontSize: '0.7rem', height: 20, cursor: 'help' }} />
                              </Tooltip>
                            )
                          }
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={parseResult.totalRows}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                sx={{ borderTop: '1px solid #E5E7EB' }}
              />
            </Paper>
          </>
        )} {/* end parseResult && file */}
      </>
    )} {/* end !jobId upload/preview section */}

        {/* ── Batch job progress + results (shown as standalone page) ── */}
        {jobId && (
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'stretch' }}>

            {/* ── Left filter panel ── */}
            {isJobDone && (
              <Paper elevation={2} sx={{ width: 230, flexShrink: 0, borderRadius: 2, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

                {/* Header */}
                <Box sx={{ px: 2, py: 1.25, backgroundColor: '#283E56', color: 'white', display: 'flex', alignItems: 'center' }}>
                  <Insights sx={{ fontSize: 15 }} />
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, ml: 0.75 }}>Filters</Typography>
                  {isFilterActive && (
                    <Button size="small" onClick={clearAllFilters}
                      sx={{ ml: 'auto', fontSize: '0.65rem', color: '#FCA5A5', textTransform: 'none', p: 0, minWidth: 0,
                        '&:hover': { color: '#F87171', backgroundColor: 'transparent' } }}>
                      Reset
                    </Button>
                  )}
                </Box>

                <Box sx={{ p: 1.75, display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto', flex: 1 }}>

                  {/* Affluence Score */}
                  <Box>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#374151', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                      Affluence Score
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                      <TextField size="small" placeholder="Min" type="number" value={minAffluence}
                        onChange={e => { setMinAffluence(e.target.value); setResultsPage(0); }}
                        sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.65, px: 0.75 },
                          '& .MuiOutlinedInput-root': { borderRadius: 1 } }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', color: '#9CA3AF', fontSize: '0.75rem' }}>–</Box>
                      <TextField size="small" placeholder="Max" type="number" value={maxAffluence}
                        onChange={e => { setMaxAffluence(e.target.value); setResultsPage(0); }}
                        sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.65, px: 0.75 },
                          '& .MuiOutlinedInput-root': { borderRadius: 1 } }} />
                    </Box>
                  </Box>

                  {/* WealthIQ Net Assets */}
                  <Box>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#374151', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                      WealthIQ Net Assets
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                      <TextField size="small" placeholder="Min" type="number" value={minWealth}
                        onChange={e => { setMinWealth(e.target.value); setResultsPage(0); }}
                        sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.65, px: 0.75 },
                          '& .MuiOutlinedInput-root': { borderRadius: 1 } }} />
                      <Box sx={{ display: 'flex', alignItems: 'center', color: '#9CA3AF', fontSize: '0.75rem' }}>–</Box>
                      <TextField size="small" placeholder="Max" type="number" value={maxWealth}
                        onChange={e => { setMaxWealth(e.target.value); setResultsPage(0); }}
                        sx={{ flex: 1, '& .MuiInputBase-input': { fontSize: '0.78rem', py: 0.65, px: 0.75 },
                          '& .MuiOutlinedInput-root': { borderRadius: 1 } }} />
                    </Box>
                  </Box>

                  {/* Est. HH Income */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                        Est. HH Income
                      </Typography>
                      {incomeFilter !== null && (
                        <Button size="small" onClick={() => setIncomeFilter(null)}
                          sx={{ fontSize: '0.62rem', color: '#6B7280', textTransform: 'none', p: 0, minWidth: 0,
                            '&:hover': { color: '#374151', backgroundColor: 'transparent' } }}>
                          All
                        </Button>
                      )}
                    </Box>
                    {BATCH_PYRAMID_TIERS.map(tier => {
                      const count = (() => {
                        if (!job?.rows) return 0;
                        return job.rows.filter(r => r.status === 'completed' && getBatchRowTier(r.est_hh_income) === tier.label).length;
                      })();
                      const checked = incomeFilter === null || incomeFilter.has(tier.label);
                      return (
                        <Box key={tier.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.25, opacity: count === 0 ? 0.4 : 1 }}>
                          <Checkbox size="small"
                            checked={checked}
                            disabled={count === 0}
                            onChange={() => {
                              setResultsPage(0);
                              setIncomeFilter(prev => {
                                const base = prev ?? new Set(BATCH_PYRAMID_TIERS.map(t => t.label));
                                const next = new Set(base);
                                next.has(tier.label) ? next.delete(tier.label) : next.add(tier.label);
                                return next.size === BATCH_PYRAMID_TIERS.length ? null : next;
                              });
                            }}
                            sx={{ p: 0.2, color: tier.color, '&.Mui-checked': { color: tier.color } }}
                          />
                          <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: tier.color, flexShrink: 0 }} />
                          <Typography sx={{ fontSize: '0.68rem', color: '#374151', flex: 1, lineHeight: 1.3 }}>{tier.label}</Typography>
                          <Typography sx={{ fontSize: '0.67rem', color: '#9CA3AF', fontWeight: 600, flexShrink: 0 }}>{count}</Typography>
                        </Box>
                      );
                    })}
                  </Box>

                </Box>
              </Paper>
            )}

            {/* Main content column */}
            <Box sx={{ flex: 1, minWidth: 0 }}>

            {/* Error alert */}
            {runError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRunError(null)}>{runError}</Alert>}

            {/* Loading state before first poll returns */}
            {!job && !runError && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress sx={{ color: '#283E56' }} />
              </Box>
            )}

            {/* Progress card */}
            {job && (<>

            {/* ── Status bar ── */}
            <Paper elevation={2} sx={{ px: 2.5, py: 1.75, borderRadius: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                {!isJobDone
                  ? <CircularProgress size={16} sx={{ color: '#283E56' }} />
                  : job.status === 'completed'
                  ? <CheckCircle sx={{ color: '#22c55e', fontSize: 18 }} />
                  : <ErrorIcon sx={{ color: '#ef4444', fontSize: 18 }} />
                }
                <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#283E56' }}>
                  {!isJobDone ? 'Screening in progress…' : job.status === 'completed' ? 'Screening complete' : 'Screening finished with errors'}
                </Typography>
                <Chip label={job.status.charAt(0).toUpperCase() + job.status.slice(1)} size="small"
                  sx={{ fontWeight: 600, fontSize: '0.7rem', height: 20,
                    ...(job.status === 'completed' ? { backgroundColor: '#dcfce7', color: '#166534' }
                      : job.status === 'running'   ? { backgroundColor: '#eff6ff', color: '#1d4ed8' }
                      : job.status === 'failed'    ? { backgroundColor: '#fef2f2', color: '#991b1b' }
                      :                              { backgroundColor: '#F3F4F6', color: '#6B7280' }),
                  }} />
              </Box>
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <LinearProgress variant="determinate" value={progressPct} sx={{
                  flex: 1, height: 6, borderRadius: 99, backgroundColor: '#E5E7EB',
                  '& .MuiLinearProgress-bar': { backgroundColor: job.status === 'failed' ? '#ef4444' : '#00C853', borderRadius: 99 },
                }} />
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, whiteSpace: 'nowrap', minWidth: 36, textAlign: 'right',
                  color: job.status === 'failed' ? '#dc2626' : progressPct === 100 ? '#16a34a' : '#283E56' }}>
                  {progressPct}%
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0, pl: 1, borderLeft: '1px solid #E5E7EB' }}>
                {[
                  { label: 'Total',     value: job.total_rows,     color: '#374151' },
                  { label: 'Done',      value: job.completed_rows,  color: '#16a34a' },
                  { label: 'Failed',    value: job.failed_rows,     color: job.failed_rows > 0 ? '#dc2626' : '#9CA3AF' },
                  { label: 'Pending',   value: Math.max(0, job.total_rows - job.completed_rows - job.failed_rows), color: '#9CA3AF' },
                ].map(s => (
                  <Box key={s.label} sx={{ textAlign: 'center', minWidth: 36 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: s.color, lineHeight: 1.1 }}>{s.value}</Typography>
                    <Typography sx={{ fontSize: '0.62rem', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.3 }}>{s.label}</Typography>
                  </Box>
                ))}
              </Box>
            </Paper>

            {/* ── Stat cards (only when done) ── */}
            {isJobDone && job.rows && (() => {
              const completedRows = job.rows.filter(r => r.status === 'completed');

              // Income distribution
              const bandCounts: Record<string, number> = {};
              for (const r of completedRows) {
                const key = getBatchRowTier(r.est_hh_income);
                bandCounts[key] = (bandCounts[key] ?? 0) + 1;
              }
              const topBand = BATCH_PYRAMID_TIERS.find(t => (bandCounts[t.label] ?? 0) > 0);

              // Home value range
              const homeValues = completedRows
                .map(r => r.home_value ? parseFloat(r.home_value.replace(/[$,]/g, '')) : null)
                .filter((v): v is number => v !== null && !isNaN(v));
              const sortedHV = [...homeValues].sort((a, b) => a - b);
              const minHV = sortedHV.length > 0 ? sortedHV[0] : null;
              const maxHV = sortedHV.length > 0 ? sortedHV[sortedHV.length - 1] : null;
              const medianHV = sortedHV.length > 0
                ? sortedHV.length % 2 === 0
                  ? (sortedHV[sortedHV.length / 2 - 1] + sortedHV[sortedHV.length / 2]) / 2
                  : sortedHV[Math.floor(sortedHV.length / 2)]
                : null;
              const fmtMoney = (v: number) =>
                v >= 1_000_000 ? `$${(v / 1_000_000).toFixed(1)}M` : `$${Math.round(v / 1000)}K`;

              return (
                <Box sx={{ display: 'grid', gridTemplateColumns: '0.5fr 1.5fr 2fr', gap: 2, mb: 2 }}>

                  {/* Card: Top Income Band */}
                  {(() => {
                    const topBandRows = topBand
                      ? completedRows.filter(r => getBatchRowTier(r.est_hh_income) === topBand.label)
                      : [];
                    const tooltipContent = topBand && topBandRows.length > 0 ? (
                      <Box sx={{ p: 0.5, maxHeight: 260, overflowY: 'auto' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1, pb: 0.75, borderBottom: `2px solid ${topBand.color}` }}>
                          <Box sx={{ width: 9, height: 9, borderRadius: '50%', backgroundColor: topBand.color, flexShrink: 0 }} />
                          <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, color: topBand.color, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            {topBand.label}
                          </Typography>
                        </Box>
                        {topBandRows.map(r => {
                          const name = [r.first_name, r.last_name].filter(Boolean).join(' ') || '—';
                          const hv = r.home_value ?? '—';
                          return (
                            <Box key={r.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.4, borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
                              <Typography sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap', color: '#fff', fontWeight: 500 }}>{name}</Typography>
                              <Typography sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap', color: '#fff', fontWeight: 700 }}>{hv}</Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : '';
                    return (
                      <Tooltip title={tooltipContent} placement="top" arrow
                        componentsProps={{ tooltip: { sx: { backgroundColor: '#1a2535', maxWidth: 320, borderRadius: 1.5, boxShadow: `0 4px 24px rgba(0,0,0,0.35), 0 0 0 1px ${topBand?.color}33` } }, arrow: { sx: { color: '#1a2535' } } }}>
                        <Paper elevation={2} sx={{ borderRadius: 2, p: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center', cursor: topBand ? 'default' : 'default' }}>
                          <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#4B5563', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>Top Income Band</Typography>
                          {topBand ? (
                            <>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: topBand.color, flexShrink: 0 }} />
                                <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#283E56', lineHeight: 1.2 }}>{topBand.label}</Typography>
                              </Box>
                              <Typography sx={{ fontSize: '0.72rem', color: '#6B7280' }}>{bandCounts[topBand.label]} prospect{bandCounts[topBand.label] !== 1 ? 's' : ''}</Typography>
                            </>
                          ) : <Typography sx={{ fontSize: '0.9rem', color: '#9CA3AF' }}>—</Typography>}
                        </Paper>
                      </Tooltip>
                    );
                  })()}

                  {/* Card: Home Value Range */}
                  <Paper elevation={2} sx={{ borderRadius: 2, p: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#4B5563', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>Home Value Range</Typography>
                    {minHV !== null && maxHV !== null ? (() => {
                      const range = maxHV - minHV || 1;
                      const medPct = medianHV !== null ? ((medianHV - minHV) / range) * 100 : 50;
                      const countBelowMedian = sortedHV.filter(v => v <= (medianHV ?? 0)).length;
                      const countAboveMedian = sortedHV.filter(v => v > (medianHV ?? 0)).length;
                      const DOTS = [
                        { pct: 0,      value: minHV,    label: 'Min',    color: '#6B7280' },
                        { pct: medPct, value: medianHV!, label: 'Median', color: '#2EC4A5' },
                        { pct: 100,    value: maxHV,    label: 'Max',    color: '#0E6655' },
                      ];
                      return (
                        <>
                          {/* Number line */}
                          <Box sx={{ position: 'relative', mx: 1, mt: 2, mb: 3.5 }}>
                            {/* Track — two colored segments connecting the dots */}
                            <Box sx={{ height: 2, backgroundColor: '#E5E7EB', borderRadius: 99, position: 'relative' }}>
                              <Box sx={{ position: 'absolute', left: 0, top: 0, height: '100%', width: `${medPct}%`,
                                background: 'linear-gradient(to right, #9CA3AF, #2EC4A5)' }} />
                              <Box sx={{ position: 'absolute', left: `${medPct}%`, top: 0, height: '100%', width: `${100 - medPct}%`,
                                background: 'linear-gradient(to right, #2EC4A5, #0E6655)' }} />
                            </Box>
                            {/* Between-band counts above track — clickable to filter */}
                            {([
                              { key: 'lower' as const, pct: medPct / 2, count: countBelowMedian },
                              { key: 'upper' as const, pct: (medPct + 100) / 2, count: countAboveMedian },
                            ]).map(({ key, pct, count }) => {
                              const isActive = homeValueRange === key;
                              return (
                                <Box key={key}
                                  onClick={() => { setHomeValueRange(isActive ? null : key); setResultsPage(0); }}
                                  sx={{ position: 'absolute', left: `${pct}%`, top: -24,
                                    transform: 'translateX(-50%)', whiteSpace: 'nowrap', cursor: 'pointer',
                                    backgroundColor: isActive ? '#2EC4A5' : '#F0FAFA',
                                    borderRadius: 1.5, px: 1, py: 0.25,
                                    border: `1px solid ${isActive ? '#27AE93' : '#A7E3DA'}`,
                                    boxShadow: isActive ? '0 1px 6px rgba(46,196,165,0.35)' : '0 1px 3px rgba(0,0,0,0.06)',
                                    transition: 'all 0.15s',
                                    '&:hover': { backgroundColor: isActive ? '#27AE93' : '#D9F2EE', borderColor: isActive ? '#1A8A72' : '#2EC4A5', transform: 'translateX(-50%) translateY(-1px)' } }}>
                                  <Typography sx={{ fontSize: '0.62rem', color: isActive ? '#fff' : '#0E6655', fontWeight: 600 }}>
                                    {count} prospect{count !== 1 ? 's' : ''}
                                  </Typography>
                                </Box>
                              );
                            })}
                            {/* Dots + labels */}
                            {DOTS.map(({ pct, value, label, color }) => (
                              <Box key={label} sx={{ position: 'absolute', top: '50%', left: `${pct}%`,
                                transform: 'translate(-50%, -50%)', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                <Box sx={{ width: label === 'Median' ? 12 : 10, height: label === 'Median' ? 12 : 10,
                                  borderRadius: '50%', backgroundColor: color, border: '2px solid #fff',
                                  boxShadow: `0 0 0 2px ${color}55`, zIndex: 1 }} />
                                <Box sx={{ position: 'absolute', top: label === 'Median' ? 14 : 13,
                                  display: 'flex', flexDirection: 'column', alignItems: 'center', whiteSpace: 'nowrap' }}>
                                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#283E56', lineHeight: 1.1 }}>{fmtMoney(value)}</Typography>
                                  <Typography sx={{ fontSize: '0.57rem', color, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>{label}</Typography>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                          <Typography sx={{ alignSelf: 'flex-end', fontSize: '0.65rem', color: '#9CA3AF', mt: 0.5 }}>*{homeValues.length} prospects with data</Typography>
                        </>
                      );
                    })() : <Typography sx={{ fontSize: '0.9rem', color: '#9CA3AF' }}>No data</Typography>}
                  </Paper>

                  {/* Card: Income Distribution chart */}
                  {(() => {
                    const activeTiers = BATCH_PYRAMID_TIERS.filter(t => (bandCounts[t.label] ?? 0) > 0);
                    const total = activeTiers.reduce((s, t) => s + (bandCounts[t.label] ?? 0), 0);
                    return (
                      <Paper elevation={2} sx={{ borderRadius: 2, p: 2, display: 'flex', flexDirection: 'column' }}>
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#4B5563', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>Income Distribution</Typography>
                        {/* Stacked bar */}
                        <Box sx={{ display: 'flex', height: 22, borderRadius: 99, overflow: 'hidden', mb: 1.5 }}>
                          {activeTiers.map(tier => {
                            const pct = ((bandCounts[tier.label] ?? 0) / total) * 100;
                            const isOnlySelected = incomeFilter !== null && incomeFilter.size === 1 && incomeFilter.has(tier.label);
                            const isDimmed = incomeFilter !== null && !incomeFilter.has(tier.label);
                            return (
                              <Tooltip key={tier.label} title={`${tier.label}: ${bandCounts[tier.label]} — click to filter`} placement="top" arrow>
                                <Box onClick={() => {
                                    setResultsPage(0);
                                    setIncomeFilter(prev => {
                                      if (prev !== null && prev.size === 1 && prev.has(tier.label)) return null;
                                      return new Set([tier.label]);
                                    });
                                  }}
                                  sx={{ width: `${pct}%`, backgroundColor: tier.color, cursor: 'pointer',
                                    opacity: isDimmed ? 0.35 : 1,
                                    outline: isOnlySelected ? '2px solid white' : 'none',
                                    outlineOffset: '-2px',
                                    transition: 'opacity 0.2s, filter 0.15s',
                                    '&:hover': { filter: 'brightness(1.2)' } }} />
                              </Tooltip>
                            );
                          })}
                        </Box>
                        {/* Legend */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px' }}>
                          {activeTiers.map(tier => {
                            const pct = Math.round(((bandCounts[tier.label] ?? 0) / total) * 100);
                            const isDimmed = incomeFilter !== null && !incomeFilter.has(tier.label);
                            return (
                              <Box key={tier.label} onClick={() => {
                                  setResultsPage(0);
                                  setIncomeFilter(prev => {
                                    if (prev !== null && prev.size === 1 && prev.has(tier.label)) return null;
                                    return new Set([tier.label]);
                                  });
                                }}
                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer',
                                  opacity: isDimmed ? 0.4 : 1, transition: 'opacity 0.2s',
                                  '&:hover': { opacity: 1 } }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: tier.color, flexShrink: 0 }} />
                                <Typography sx={{ fontSize: '0.67rem', color: '#374151', whiteSpace: 'nowrap' }}>{tier.label} <strong>{pct}%</strong></Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      </Paper>
                    );
                  })()}

                </Box>
              );
            })()}

            {/* Per-row status table */}
            {job.rows && job.rows.length > 0 && (() => {
              const completedRows = job.rows.filter(r => r.status === 'completed');
              return (
              <>
                <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ px: 2.5, py: 2, backgroundColor: '#283E56', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Experian Results</Typography>
                    {isFilterActive && (
                      <Chip
                        label={`${pyramidFilteredRows.length} of ${sortedResultRows.length}`}
                        size="small"
                        onDelete={clearAllFilters}
                        deleteIcon={<Close sx={{ fontSize: '12px !important', color: 'white !important' }} />}
                        sx={{ backgroundColor: '#00C853', color: 'white', fontWeight: 700, height: 20, fontSize: '0.7rem',
                          '& .MuiChip-deleteIcon': { color: 'white' } }}
                      />
                    )}
                  </Box>
                  {isJobDone && (
                    <Typography variant="caption" sx={{ opacity: 0.6 }}>
                      {nextStageRows.size > 0 ? `${nextStageRows.size} selected` : 'Check rows to add to a view'}
                    </Typography>
                  )}
                </Box>
                <TableContainer sx={{ maxHeight: 500 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#F8FAFC' }}>
                        {isJobDone && (
                          <TableCell padding="checkbox" sx={{ backgroundColor: '#F8FAFC', width: 44 }}>
                            <Tooltip title={allNextSelected ? 'Deselect all' : 'Select all completed'}>
                              <Checkbox
                                size="small"
                                checked={allNextSelected}
                                indeterminate={someNextSelected}
                                onChange={handleToggleAllNext}
                                disabled={completedRows.length === 0}
                                sx={{ color: '#283E56', '&.Mui-checked': { color: '#283E56' }, '&.MuiCheckbox-indeterminate': { color: '#283E56' } }}
                              />
                            </Tooltip>
                          </TableCell>
                        )}
                        <TableCell sx={{ fontWeight: 700, color: '#283E56', backgroundColor: '#F8FAFC', whiteSpace: 'nowrap' }}>#</TableCell>
                        <Tooltip title={sortField === 'last_name' ? `Sorted ${sortDir === 'asc' ? 'A → Z' : 'Z → A'} — click to reverse` : 'Click to sort A–Z'} placement="top">
                          <TableCell sx={{ fontWeight: 700, color: '#283E56', backgroundColor: '#F8FAFC', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}>
                            <TableSortLabel
                              active={sortField === 'last_name'}
                              direction={sortField === 'last_name' ? sortDir : 'asc'}
                              onClick={() => handleSort('last_name')}
                              sx={{
                                '&.Mui-active': { color: '#283E56' },
                                '& .MuiTableSortLabel-icon': { color: '#283E56 !important', opacity: sortField === 'last_name' ? 1 : 0.35 },
                                '&:hover .MuiTableSortLabel-icon': { opacity: 1 },
                              }}
                            >
                              Name
                            </TableSortLabel>
                          </TableCell>
                        </Tooltip>
                        <TableCell sx={{ fontWeight: 700, color: '#283E56', backgroundColor: '#F8FAFC', whiteSpace: 'nowrap' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 700, color: '#283E56', backgroundColor: '#F8FAFC', whiteSpace: 'nowrap' }}>Address</TableCell>
                        {([
                          { label: 'Affluence Score',    field: 'affluence_score'  as SortField },
                          { label: 'WealthIQ Net Assets', field: 'wealth_iq_score' as SortField },
                          { label: 'Est. HH Income',     field: 'est_hh_income'   as SortField },
                          { label: 'Home Value',         field: 'home_value'      as SortField },
                        ]).map(({ label, field }) => (
                          <Tooltip key={field} title={sortField === field ? `Sorted ${sortDir === 'asc' ? 'low → high' : 'high → low'} — click to reverse` : 'Click to sort'} placement="top">
                            <TableCell sx={{ fontWeight: 700, color: '#283E56', backgroundColor: '#F8FAFC', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' }}>
                              <TableSortLabel
                                active={sortField === field}
                                direction={sortField === field ? sortDir : 'asc'}
                                onClick={() => handleSort(field)}
                                sx={{
                                  '&.Mui-active': { color: '#283E56' },
                                  '& .MuiTableSortLabel-icon': { color: '#283E56 !important', opacity: sortField === field ? 1 : 0.35 },
                                  '&:hover .MuiTableSortLabel-icon': { opacity: 1 },
                                }}
                              >
                                {label}
                              </TableSortLabel>
                            </TableCell>
                          </Tooltip>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedResultRows.map((r: BatchRowStatus) => {
                        const isCompleted = r.status === 'completed';
                        const isChecked = nextStageRows.has(r.id);
                        return (
                          <TableRow
                            key={r.id}
                            onClick={() => isJobDone && isCompleted && handleToggleNextStage(r.id)}
                            sx={{
                              cursor: isJobDone && isCompleted ? 'pointer' : 'default',
                              backgroundColor: isChecked ? 'rgba(0,200,83,0.05)' : 'inherit',
                              '&:hover': { backgroundColor: isJobDone && isCompleted ? 'rgba(0,200,83,0.08)' : 'rgba(40,62,86,0.02)' },
                            }}
                          >
                            {isJobDone && (
                              <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                                <Checkbox
                                  size="small"
                                  checked={isChecked}
                                  disabled={!isCompleted}
                                  onChange={() => handleToggleNextStage(r.id)}
                                  sx={{ color: '#00C853', '&.Mui-checked': { color: '#00C853' } }}
                                />
                              </TableCell>
                            )}
                            <TableCell sx={{ color: '#9CA3AF', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{r.row_index}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {[r.first_name, r.last_name].filter(Boolean).join(' ')}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {r.status === 'pending' && !isJobDone
                                ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}><HourglassEmpty sx={{ fontSize: 14, color: '#9CA3AF' }} /><Typography variant="caption" sx={{ color: '#9CA3AF' }}>Waiting</Typography></Box>
                                : r.status === 'running'
                                ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}><CircularProgress size={12} /><Typography variant="caption" sx={{ color: '#1d4ed8' }}>Running</Typography></Box>
                                : r.status === 'completed'
                                ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}><CheckCircle sx={{ fontSize: 14, color: '#22c55e' }} /><Typography variant="caption" sx={{ color: '#166534' }}>Done</Typography></Box>
                                : <Tooltip title={r.error_message ?? 'Unknown error'}><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'help' }}><ErrorIcon sx={{ fontSize: 14, color: '#ef4444' }} /><Typography variant="caption" sx={{ color: '#991b1b' }}>Failed</Typography></Box></Tooltip>
                              }
                            </TableCell>
                            {/* ── Address column ── */}
                            <TableCell sx={{ fontSize: '0.82rem', color: '#374151', whiteSpace: 'nowrap' }}>
                              {[r.street, r.city, [r.state, r.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ') ||
                                <Typography component="span" sx={{ color: '#CBD5E0', fontSize: '0.78rem' }}>—</Typography>}
                            </TableCell>
                            {/* ── Experian data columns ── */}
                            {(['affluence_score', 'wealth_iq_score', 'est_hh_income', 'home_value'] as const).map(field => (
                              <TableCell key={field} sx={{ fontSize: '0.82rem', color: '#283E56', fontWeight: r[field] ? 600 : 400, whiteSpace: 'nowrap' }}>
                                {r[field] ?? <Typography component="span" sx={{ color: '#CBD5E0', fontSize: '0.78rem' }}>—</Typography>}
                              </TableCell>
                            ))}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={pyramidFilteredRows.length}
                  page={resultsPage}
                  rowsPerPage={resultsRowsPerPage}
                  rowsPerPageOptions={[50, 100, 250]}
                  onPageChange={(_, p) => setResultsPage(p)}
                  onRowsPerPageChange={e => { setResultsRowsPerPage(parseInt(e.target.value, 10)); setResultsPage(0); }}
                  sx={{ borderTop: '1px solid #E5E7EB' }}
                />
              </Paper>
              </>
              );
            })()}
            </>)}

            </Box>{/* end main content column */}
          </Box>
        )}
      </Box>

      {/* ── Create View Dialog ── */}
      <Dialog open={createViewOpen} onClose={() => !isCreatingView && setCreateViewOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#283E56' }}>
          Create Prospect View
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#6B7280', mb: 2 }}>
            {isFilterActive
              ? <>Name this view of <strong>{pyramidFilteredRows.length}</strong> filtered prospect{pyramidFilteredRows.length !== 1 ? 's' : ''}.<br/>Active filters have been applied — only matching results will be saved.</>
              : <>Name this view of <strong>{nextStageRows.size}</strong> selected prospect{nextStageRows.size !== 1 ? 's' : ''}.</>
            }
            {' '}You can run Apollo, FEC, and IRS enrichment from the view page.
          </Typography>
          <TextField
            autoFocus
            label="View name"
            placeholder="e.g. Major Donors Q2 2026"
            fullWidth
            value={viewName}
            onChange={e => setViewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && viewName.trim() && handleCreateView()}
            disabled={isCreatingView}
            size="small"
          />
          {createViewError && (
            <Alert severity="error" sx={{ mt: 2 }}>{createViewError}</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCreateViewOpen(false)}
            disabled={isCreatingView}
            sx={{ color: '#6B7280' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateView}
            disabled={!viewName.trim() || isCreatingView}
            startIcon={isCreatingView ? <CircularProgress size={14} sx={{ color: 'white' }} /> : null}
            sx={{ backgroundColor: '#283E56', '&:hover': { backgroundColor: '#1C2433' } }}
          >
            {isCreatingView ? 'Creating…' : 'Create View'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BatchPage;
